import { useRouter } from '@tanstack/react-router';
import { mapApiErrorToForm } from '@upme/api-client';
import { CityCreateInput, type CityDto, CityUpdateInput } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { Input } from '@upme/ui/components/input';
import { Label } from '@upme/ui/components/label';
import { Switch } from '@upme/ui/components/switch';
import { useState } from 'react';
import { type Resolver, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { zodResolver } from '../../../app/zod-resolver';
import { useCreateCity, useUpdateCity } from '../mutations';
import { CountryCombobox } from './country-combobox';
import { CurrencyCombobox } from './currency-combobox';
import { TimezoneCombobox } from './timezone-combobox';

type CreateProps = { mode: 'create'; defaultValues?: Partial<CityCreateInput> };
type EditProps = { mode: 'edit'; city: CityDto };
type Props = CreateProps | EditProps;

type FormValues = CityCreateInput & Partial<CityCreateInput>;

/** Доллары строкой → центы; '' → null; некорректное → undefined (ошибка формы). */
function parseMinReward(raw: string): number | null | undefined {
  const t = raw.trim().replace(',', '.');
  if (t === '') return null;
  const cents = Math.round(Number(t) * 100);
  return Number.isFinite(cents) && cents >= 1 && cents <= 100_000 ? cents : undefined;
}

export function CityForm(props: Props) {
  const router = useRouter({ warn: false });
  const isCreate = props.mode === 'create';
  // In edit mode RHF stores the full CityDto (id, slug, createdAt…) but
  // CityUpdateInput is .strict() — validation would reject those keys and
  // silently abort handleSubmit. Loosen the schema for the form layer; the
  // backend still enforces strict on the wire.
  const schema = isCreate ? CityCreateInput : CityUpdateInput.loose();

  const defaults: Partial<FormValues> = isCreate
    ? ({ isActive: true, ...(props.defaultValues ?? {}) } as Partial<FormValues>)
    : (props.city as FormValues);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema as unknown as z.ZodType<FormValues>) as Resolver<FormValues>,
    defaultValues: defaults as FormValues,
    mode: 'onSubmit',
  });

  const initialMin = isCreate ? null : (props.city.minPartnerRewardUsdCents ?? null);
  const [minRewardUsd, setMinRewardUsd] = useState(initialMin == null ? '' : (initialMin / 100).toFixed(2));
  const [minRewardError, setMinRewardError] = useState<string | null>(null);

  const create = useCreateCity();
  const update = useUpdateCity(isCreate ? '' : props.city.id);

  const onSubmit = form.handleSubmit(
    async (values) => {
      const minCents = parseMinReward(minRewardUsd);
      if (minCents === undefined) {
        setMinRewardError('Укажите сумму от $0.01 до $1000 или оставьте пустым');
        return;
      }
      try {
        let result: CityDto;
        if (isCreate) {
          result = await create.mutateAsync({
            ...values,
            minPartnerRewardUsdCents: minCents,
          } as CityCreateInput);
        } else {
          // Send only mutable fields — slug/id/timestamps are read-only.
          const body: CityUpdateInput = {
            name: values.name,
            countryCode: values.countryCode,
            timezone: values.timezone,
            locale: values.locale,
            currency: values.currency,
            isActive: values.isActive,
            googlePlaceId: values.googlePlaceId ?? null,
            minPartnerRewardUsdCents: minCents,
          };
          result = await update.mutateAsync(body);
        }
        toast.success(isCreate ? 'Город создан' : 'Изменения сохранены');
        router?.navigate({ to: '/cities/$cityId', params: { cityId: result.id } });
      } catch (err) {
        await mapApiErrorToForm(err, form, { rootField: 'slug' });
      }
    },
    (errors) => {
      // Без второго колбэка handleSubmit «молча» падает на ошибках Zod —
      // юзер видит «кнопка ничего не делает». Сюда заходим всякий раз,
      // когда validation провалилась: ловим первую ошибку и показываем
      // toast, чтобы не оставлять submit невидимым.
      const first = Object.values(errors).find(
        (e): e is { message?: string } => typeof e === 'object' && e !== null && 'message' in e,
      );
      const msg = first?.message ? String(first.message) : 'Не все поля заполнены корректно';
      toast.error(`Проверьте форму: ${msg}`);
    },
  );

  const errors = form.formState.errors;
  const slugError = errors.slug;
  const nameError = errors.name;
  const countryError = errors.countryCode;
  const timezoneError = errors.timezone;
  const localeError = errors.locale;
  const currencyError = errors.currency;
  const rootServer = (errors.root as { server?: { message?: string } } | undefined)?.server;

  return (
    <form onSubmit={onSubmit} className="space-y-4 max-w-xl" noValidate>
      <div>
        <Label htmlFor="slug">Slug</Label>
        <Input id="slug" {...form.register('slug')} disabled={!isCreate} placeholder="dubai" />
        {slugError && <p className="text-sm text-destructive mt-1">{String(slugError.message)}</p>}
      </div>

      <div>
        <Label htmlFor="name">Название</Label>
        <Input id="name" {...form.register('name')} placeholder="Дубай" />
        {nameError && <p className="text-sm text-destructive mt-1">{String(nameError.message)}</p>}
      </div>

      <div>
        <Label htmlFor="countryCode">Страна</Label>
        <CountryCombobox
          value={form.watch('countryCode')}
          onChange={(v) => form.setValue('countryCode', v, { shouldValidate: true })}
        />
        {countryError && <p className="text-sm text-destructive mt-1">{String(countryError.message)}</p>}
      </div>

      <div>
        <Label htmlFor="timezone">Часовой пояс</Label>
        <TimezoneCombobox
          value={form.watch('timezone')}
          onChange={(v) => form.setValue('timezone', v, { shouldValidate: true })}
        />
        {timezoneError && <p className="text-sm text-destructive mt-1">{String(timezoneError.message)}</p>}
      </div>

      <div>
        <Label htmlFor="locale">Локаль</Label>
        <Input id="locale" {...form.register('locale')} placeholder="ru-RU" />
        <p className="text-xs text-muted-foreground mt-1">
          BCP 47, напр. <code>ru-RU</code>
        </p>
        {localeError && <p className="text-sm text-destructive mt-1">{String(localeError.message)}</p>}
      </div>

      <div>
        <Label htmlFor="currency">Валюта</Label>
        <CurrencyCombobox
          value={form.watch('currency')}
          onChange={(v) => form.setValue('currency', v, { shouldValidate: true })}
        />
        {currencyError && <p className="text-sm text-destructive mt-1">{String(currencyError.message)}</p>}
      </div>

      <div>
        <Label htmlFor="minPartnerReward">Мин. награда партнёру, $</Label>
        <Input
          id="minPartnerReward"
          inputMode="decimal"
          placeholder="без минимума"
          value={minRewardUsd}
          onChange={(e) => {
            setMinRewardUsd(e.target.value);
            setMinRewardError(null);
          }}
        />
        <p className="mt-1 text-xs text-muted-foreground">
          Партнёрская программа события в городе не включится с наградой ниже. Пусто — без ограничения.
        </p>
        {minRewardError && <p className="text-sm text-destructive mt-1">{minRewardError}</p>}
      </div>

      <div>
        <Label htmlFor="googlePlaceId">Google Place ID (опционально)</Label>
        <Input id="googlePlaceId" placeholder="ChIJ..." {...form.register('googlePlaceId')} />
        <p className="mt-1 text-xs text-muted-foreground">
          Нужен для авто-определения cityId при импорте мест из Google Maps.{' '}
          <a
            href="https://developers.google.com/maps/documentation/javascript/examples/places-placeid-finder"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            Найти Place ID
          </a>
        </p>
      </div>

      <div className="flex items-center gap-2">
        <Switch
          id="isActive"
          checked={form.watch('isActive') ?? true}
          onCheckedChange={(v) => form.setValue('isActive', v)}
        />
        <Label htmlFor="isActive">Активен</Label>
      </div>

      {rootServer?.message && <p className="text-sm text-destructive">{rootServer.message}</p>}

      <Button type="submit" disabled={form.formState.isSubmitting}>
        {isCreate ? 'Создать' : 'Сохранить'}
      </Button>
    </form>
  );
}
